import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getSupabaseClient } from '@/lib/supabase';

export interface ParsedProduct {
  id: string;
  name: string;
  sku: string | null;
  category: string | null;
  status: string;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  // Parsed from name
  capacity: string | null;
  materialHint: string | null;
  mappedCategory: string | null;
  categoryL2Code: string | null;
  categoryL1Code: string | null;
  // Availability & MOQ data (from API display fields in tags)
  availabilityStatus: 'available' | 'limited_stock' | 'check_availability' | 'made_to_order' | null;
  availabilityDisplay: string | null;
  availabilityBadgeType: string | null;
  availabilityNote: string | null;
  moq: number | null;
  moqDisplay: string | null;
  leadTimeMinDays: number | null;
  leadTimeMaxDays: number | null;
  leadTimeDisplay: string | null;
  // Product kind & delivery
  productKind: string | null;
  deliverDays: number | null;
  // Detailed specs from tags
  capacityMl: string | null;
  capacityMlTag: string | null;
  moqTag: number | null;
  // Image-search similarity score (0..1)
  similarity?: number | null;
  // Raw tags from DB for fallback image URLs
  tags?: Record<string, unknown> | null;
  // Direct column values
  displayPriceText: string | null;
  samplePriceText: string | null;
}

export type SortOption =
  | 'newest'
  | 'nameAsc'
  | 'nameDesc'
  | 'priceLow'
  | 'priceHigh'
  | 'capacityLow'
  | 'capacityHigh'
  | 'recentlyUpdated';

export type ViewMode = 'grid' | 'list';

export interface FilterState {
  search: string;
  category: string | null;
  capacities: string[];
  materials: string[];
  availabilityStatus: 'available' | 'limited_stock' | 'check_availability' | 'made_to_order' | null;
  hasMoa: boolean;
}

const MATERIAL_KEYWORDS: Record<string, string[]> = {
  Glass: ['유리', '글라스'],
  Aluminum: ['알루미늄'],
  Metal: ['금속'],
  PET: ['PET'],
  PETG: ['PETG'],
};

function parseCapacity(name: string): string | null {
  const match = name.match(/(\d+)\s*ml/i);
  return match ? `${match[1]}ml` : null;
}

function parseMaterial(name: string): string | null {
  for (const [material, keywords] of Object.entries(MATERIAL_KEYWORDS)) {
    if (keywords.some((kw) => name.includes(kw))) {
      return material;
    }
  }
  return null;
}

function parseRow(row: Record<string, unknown>, imageUrl: string | null): ParsedProduct {
  const tags = row.tags as Record<string, unknown> | null;
  const name = (row.name as string) || '';

  const availabilityStatusRaw = (tags?.availability_status as string | undefined) || null;
  const availabilityStatus =
    availabilityStatusRaw === 'available' ||
    availabilityStatusRaw === 'limited_stock' ||
    availabilityStatusRaw === 'check_availability' ||
    availabilityStatusRaw === 'made_to_order'
      ? availabilityStatusRaw
      : null;

  const categoryL1Code = (tags?.category_l1_code as string | undefined) || null;
  const categoryL2Code = (tags?.category_l2_code as string | undefined) || null;

  return {
    id: row.id as string,
    name,
    sku: row.sku as string | null,
    category: row.category as string | null,
    status: row.status as string,
    imageUrl,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    capacity: parseCapacity(name),
    materialHint: parseMaterial(name),
    mappedCategory: categoryL2Code,
    categoryL2Code,
    categoryL1Code,
    availabilityStatus,
    availabilityDisplay: (tags?.availability_display as string | undefined) || null,
    availabilityBadgeType: (tags?.availability_badge_type as string | undefined) || null,
    availabilityNote: (tags?.availability_note as string | undefined) || null,
    moq: (tags?.moq as number | undefined) ?? null,
    moqDisplay: (tags?.moq_display as string | undefined) || null,
    leadTimeMinDays: (tags?.lead_time_min_days as number | undefined) ?? null,
    leadTimeMaxDays: (tags?.lead_time_max_days as number | undefined) ?? null,
    leadTimeDisplay: (tags?.lead_time_display as string | undefined) || null,
    productKind: (row.product_kind as string | undefined) || null,
    deliverDays: (tags?.deliver as number | undefined) ?? null,
    capacityMl: (tags?.specs as Record<string, unknown> | null)?.capacity_ml as string | null ?? null,
    capacityMlTag: (tags?.Capacity_ml as string | undefined) || null,
    moqTag: (tags?.moq as number | undefined) ?? null,
    displayPriceText: (row.display_price_text as string | undefined) || null,
    samplePriceText: (row.sample_price_text as string | undefined) || null,
    tags,
  };
}

async function fetchImagesForProducts(productIds: string[]): Promise<Record<string, string>> {
  const imagesMap: Record<string, string> = {};
  if (productIds.length === 0) return imagesMap;

  const supabase = getSupabaseClient();
  if (!supabase) return imagesMap;

  const { data: imagesData } = await supabase
    .from('product_images')
    .select('product_id, public_url, storage_bucket, storage_path, image_role, is_primary, sort_order')
    .in('product_id', productIds)
    .not('image_role', 'eq', 'original');

  if (!imagesData || imagesData.length === 0) return imagesMap;

  // Group by product_id
  const grouped: Record<string, typeof imagesData> = {};
  imagesData.forEach((img) => {
    if (!grouped[img.product_id]) grouped[img.product_id] = [];
    grouped[img.product_id].push(img);
  });

  // For each product: working > is_primary > sort_order
  Object.entries(grouped).forEach(([productId, imgs]) => {
    const sorted = [...imgs].sort((a, b) => {
      // 1) working images first
      if (a.image_role === 'working' && b.image_role !== 'working') return -1;
      if (a.image_role !== 'working' && b.image_role === 'working') return 1;
      // 2) then is_primary descending
      if (a.is_primary !== b.is_primary) return (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0);
      // 3) then sort_order ascending
      return (a.sort_order || 0) - (b.sort_order || 0);
    });

    const best = sorted[0];
    if (!best) return;

    if (best.public_url) {
      imagesMap[productId] = best.public_url;
    } else if (best.storage_bucket && best.storage_path) {
      const { data: publicUrlData } = supabase.storage
        .from(best.storage_bucket)
        .getPublicUrl(best.storage_path);
      if (publicUrlData?.publicUrl) {
        imagesMap[productId] = publicUrlData.publicUrl;
      }
    }
  });

  return imagesMap;
}

export function useProducts(overrideProducts?: ParsedProduct[] | null) {
  const [products, setProducts] = useState<ParsedProduct[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Aggregation data (loaded once, lightweight — names + tags only)
  const [availableCapacities, setAvailableCapacities] = useState<string[]>([]);
  const [availableMaterials, setAvailableMaterials] = useState<string[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    category: null,
    capacities: [],
    materials: [],
    availabilityStatus: null,
    hasMoa: false,
  });

  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 16;

  const abortRef = useRef<AbortController | null>(null);

  // ── Fetch aggregations once (names + tags for all products — lightweight) ──
  useEffect(() => {
    if (overrideProducts != null) return;

    let cancelled = false;

    const fetchAggregations = async () => {
      const supabase = getSupabaseClient();
      if (!supabase) return;

      const { data, error: dbError } = await supabase
        .from('products')
        .select('name, tags')
        .eq('status', 'active');

      if (dbError || cancelled) return;

      // Parse capacities
      const caps = new Set<string>();
      (data || []).forEach((row: Record<string, unknown>) => {
        const cap = parseCapacity((row.name as string) || '');
        if (cap) caps.add(cap);
      });

      // Parse materials
      const mats = new Set<string>();
      (data || []).forEach((row: Record<string, unknown>) => {
        const mat = parseMaterial((row.name as string) || '');
        if (mat) mats.add(mat);
      });

      // Compute category counts
      const counts: Record<string, number> = {};
      (data || []).forEach((row: Record<string, unknown>) => {
        const tags = row.tags as Record<string, unknown> | null;
        const l2Code = (tags?.category_l2_code as string | undefined) || null;
        if (l2Code) {
          counts[l2Code] = (counts[l2Code] || 0) + 1;
        }
      });

      if (!cancelled) {
        setAvailableCapacities(
          Array.from(caps).sort((a, b) => {
            const na = parseInt(a, 10) || 0;
            const nb = parseInt(b, 10) || 0;
            return na - nb;
          }),
        );
        setAvailableMaterials(Array.from(mats));
        setCategoryCounts(counts);
      }
    };

    fetchAggregations();

    return () => {
      cancelled = true;
    };
  }, [overrideProducts]);

  // ── Stable filter key for effect dependency ──
  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        search: filters.search,
        category: filters.category,
        capacities: [...filters.capacities].sort(),
        materials: [...filters.materials].sort(),
        availabilityStatus: filters.availabilityStatus,
        hasMoa: filters.hasMoa,
      }),
    [filters],
  );

  // ── Server-side fetch on filter / sort / page change ──
  useEffect(() => {
    // Image search override: skip server fetch, use provided products directly
    if (overrideProducts != null) {
      setProducts(overrideProducts);
      setLoading(false);
      setTotalCount(overrideProducts.length);
      return;
    }

    // Cancel any in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    let cancelled = false;

    const fetchFilteredProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        const supabase = getSupabaseClient();
        if (!supabase) {
          if (!cancelled) {
            setError('Supabase client not available');
            setLoading(false);
          }
          return;
        }

        let query = supabase
          .from('products')
          .select('id, name, sku, category, status, tags, product_kind, display_price_text, sample_price_text, created_at, updated_at', {
            count: 'exact',
            head: false,
          })
          .eq('status', 'active');

        // ── Search filter ──
        if (filters.search.trim()) {
          query = query.ilike('name', `%${filters.search.trim()}%`);
        }

        // ── Category filter ──
        if (filters.category) {
          query = query.or(
            `tags->>category_l2_code.eq.${filters.category},tags->>category_l1_code.eq.${filters.category}`,
          );
        }

        // ── Capacity + Material name-based OR filters ──
        const nameOrParts: string[] = [];
        filters.capacities.forEach((c) => {
          nameOrParts.push(`name.ilike.*${c}*`);
        });
        filters.materials.forEach((m) => {
          const keywords = MATERIAL_KEYWORDS[m] || [m];
          keywords.forEach((k) => {
            nameOrParts.push(`name.ilike.*${k}*`);
          });
        });
        if (nameOrParts.length > 0) {
          query = query.or(nameOrParts.join(','));
        }

        // ── Availability filter ──
        if (filters.availabilityStatus) {
          query = query.eq('tags->>availability_status', filters.availabilityStatus);
        }

        // ── MOQ filter ──
        if (filters.hasMoa) {
          query = query.not('tags->>moq', 'is', null);
        }

        // ── Sort ──
        switch (sortBy) {
          case 'newest':
            query = query.order('created_at', { ascending: false });
            break;
          case 'recentlyUpdated':
            query = query.order('updated_at', { ascending: false });
            break;
          case 'nameAsc':
            query = query.order('name', { ascending: true });
            break;
          case 'nameDesc':
            query = query.order('name', { ascending: false });
            break;
          case 'capacityLow':
          case 'capacityHigh':
          case 'priceLow':
          case 'priceHigh':
            // Fallback: sort by newest; client-side secondary sort applied below
            query = query.order('created_at', { ascending: false });
            break;
          default:
            query = query.order('created_at', { ascending: false });
        }

        // ── Pagination ──
        const from = currentPage * pageSize;
        const to = from + pageSize - 1;
        query = query.range(from, to);

        // Apply abort signal
        if (controller.signal) {
          query = query.abortSignal(controller.signal);
        }

        const { data, count, error: dbError } = await query;

        if (cancelled) return;

        if (dbError) {
          // Ignore aborted request errors
          if (dbError.message?.includes('AbortError') || dbError.code === '20') {
            return;
          }
          throw dbError;
        }

        const serverTotal = count ?? 0;

        // Fetch images for fetched products
        const productIds = (data || []).map((row: Record<string, unknown>) => row.id as string);
        const imagesMap = await fetchImagesForProducts(productIds);

        if (cancelled) return;

        // Parse results — working image from imagesMap is the canonical source
        let parsed: ParsedProduct[] = (data || []).map((row: Record<string, unknown>) => {
          const imgUrl = imagesMap[row.id as string] || null;
          return parseRow(row, imgUrl);
        });

        // Client-side secondary sort for capacity-based sorts
        if (sortBy === 'capacityLow' || sortBy === 'capacityHigh') {
          parsed = [...parsed].sort((a, b) => {
            const ca = parseInt(a.capacity || '0', 10);
            const cb = parseInt(b.capacity || '0', 10);
            return sortBy === 'capacityLow' ? ca - cb : cb - ca;
          });
        }

        setProducts(parsed);
        setTotalCount(serverTotal);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load products');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchFilteredProducts();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [overrideProducts, filtersKey, sortBy, currentPage]);

  // ── Reset page on filter/sort change ──
  useEffect(() => {
    setCurrentPage(0);
  }, [filtersKey, sortBy]);

  // ── totalPages from server count ──
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / pageSize));
  }, [totalCount, pageSize]);

  // ── paginatedProducts = already paginated by server ──
  const paginatedProducts = products;

  const updateFilter = useCallback(
    <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      category: null,
      capacities: [],
      materials: [],
      availabilityStatus: null,
      hasMoa: false,
    });
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.category) count += 1;
    if (filters.capacities.length) count += 1;
    if (filters.materials.length) count += 1;
    if (filters.availabilityStatus) count += 1;
    if (filters.hasMoa) count += 1;
    return count;
  }, [filters]);

  return {
    products,
    allProducts: products,
    loading,
    error,
    filters,
    updateFilter,
    clearFilters,
    activeFilterCount,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    availableCapacities,
    availableMaterials,
    categoryCounts,
    currentPage,
    setCurrentPage,
    pageSize,
    totalPages,
    totalCount,
    paginatedProducts,
  };
}